import { useId } from "react";
import { Link } from "react-router";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Sentry } from "@/lib/sentry";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

/**
 * A venue as the picker holds it. A search hit carries more than the form keeps (an address, photos), so a venue the
 * form already holds is just `{ id, name }`: it shows as picked without a search.
 */
export interface VenueOption {
  id: string;
  name: string;
  address?: string;
  media?: { photos?: Array<{ url: string }> };
}

interface VenuePickerProps {
  /** The venue picked now; none shows the empty control. */
  value: VenueOption | undefined;
  /** The control's accessible name, e.g. `Venue on 11 Dec 2026`: the control itself shows only the venue. */
  label: string;
  /** A venue picked, or none when the curator clears the pick. */
  onChange: (venue: VenueOption | undefined) => void;
}

/**
 * One venue out of Mood's venues (the same search the venue HITL panel uses). A venue is picked, never typed: Tina
 * matches events to a festival day by venue id, and a typed name has none.
 */
export function VenuePicker({ value, label, onChange }: VenuePickerProps) {
  const { search } = useVenueRepository();
  const id = useId();
  const labelId = `${id}-label`;
  const triggerId = `${id}-trigger`;

  return (
    <div className="min-w-0">
      <span id={labelId} className="sr-only">
        {label}
      </span>
      <RemoteSelect<VenueOption>
        value={value}
        triggerType="change"
        showSelected
        triggerId={triggerId}
        // The name is the label, then what is picked: the trigger names itself too.
        ariaLabelledBy={`${labelId} ${triggerId}`}
        className="w-full font-normal"
        onValueChanged={onChange}
        accessorFn={(option) => ({
          id: option?.id || "",
          name: option
            ? `${option.name}${option.address ? ` (${option.address})` : ""}`
            : "",
          iconUrl: option?.media?.photos?.[0]?.url,
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          try {
            const results = (await search(query)) as VenueOption[];
            onStatusChanged("success");
            return results;
          } catch (error) {
            Sentry.captureException(error);
            onStatusChanged("error");
            return [];
          }
        }}
      />
    </div>
  );
}

/**
 * The line under a venue the curator has to pick and cannot find: a venue is picked from Mood's venues, so one Mood does
 * not have yet is created on the Venues page (in a new tab, so the form keeps its edits) and picked here afterwards.
 */
export function CreateVenueHint() {
  return (
    <p className="text-xs text-muted-foreground">
      Not in Mood yet? Create it under{" "}
      <Link
        to="/venues/create"
        target="_blank"
        rel="noreferrer"
        className="underline hover:text-foreground"
      >
        Venues
      </Link>
      , then pick it here.
    </p>
  );
}
